// @file The store for "Make" page (`DashDesktopMakeMenuPane.vue` and `DashDesktopMake.vue`)
import { trackEvent } from '@@/bits/analytics'
import { setCookie } from '@@/bits/cookie'
import { formatDate } from '@@/bits/date'
import device from '@@/bits/device'
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { p__, __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { isPartOfUnlimitedSandboxesEvent, unlimitedSandboxesEventUtcEndDate } from '@@/bits/unlimited_sandboxes_event'
import * as PadletApi from '@@/dashboard/padlet_api'
import {
  GuidedTemplateType,
  LibraryMembershipRole,
  MakePageLayoutExistingUsersExperimentVariant,
  MakePageLayoutNewTeachersExperimentVariant,
  MakePageSimplifiedLayoutExperimentVariant,
  SnackbarNotificationType,
  SubscriptionStatus,
} from '@@/enums'
import { Ab as AbApi } from '@@/payment/padlet_api'
import { useDashAccountsStore } from '@@/pinia/dash_accounts_store'
import { useDashCollectionsStore } from '@@/pinia/dash_collections_store'
import { useDashGalleryTemplatesStore } from '@@/pinia/dash_gallery_templates_store'
import { useDashMakeButtonStore } from '@@/pinia/dash_make_button_store'
import { useDashStore } from '@@/pinia/dash_store'
import { useDashSuggestedTemplatesStore } from '@@/pinia/dash_suggested_templates_store'
import { useExperimentsStore } from '@@/pinia/experiments'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useMagicPadletPanelStore } from '@@/pinia/magic_padlet_panel_store'
import { useUserAccountsStore } from '@@/pinia/user_accounts_store'
import { WallApi } from '@@/surface/api/wall'
import type {
  AiMakeSuggestion,
  LibraryId,
  WallCreationFromTemplateResult,
  WallGalleryTemplate,
  WallTemplate,
} from '@@/types'
import { useObserveVisibleElementOnScroll } from '@@/vuecomposables/observe_visible_element_on_scroll'
import type { JsonAPIResource } from '@padlet/arvo'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum DashMakePageFilter {
  START_FROM_SCRATCH = 'START_FROM_SCRATCH',
  AI_SUGGESTIONS = 'AI_SUGGESTIONS',
  AI_RECIPES = 'AI_RECIPES',
  YOUR_TEMPLATES = 'YOUR_TEMPLATES',
  RECIPES = 'RECIPES',
}

export enum WallCreatedFrom {
  Blank = 'blank',
  GalleryTemplate = 'gallery_template',
  GuidedTemplate = 'guided_template',
  Whiteboard = 'whiteboard',
  Demo = 'demo',
  Other = 'other',
  Error = 'error',
  AiMakeSuggestionBoard = 'ai_make_suggestion_board',
  AiMakeSuggestionWhiteboard = 'ai_make_suggestion_whiteboard',
  Make = 'make',
}

export enum WhiteboardCardTemplateType {
  Venn = 'venn',
  Brainstorming = 'brainstorming',
  Presentation = 'presentation',
  Question = 'question',
}

export interface WallCreationMetadata {
  createdFrom?: WallCreatedFrom
  creationEntrypoint?: 'make_page' | 'gallery_template'
}

export const DEFAULT_WALL_CREATION_METADATA: WallCreationMetadata = {
  createdFrom: WallCreatedFrom.Make,
  creationEntrypoint: 'make_page',
}

// @ts-expect-error - templateIds is added to vueStartingState in dashboard_controller.rb
const PRESENTATION_TEMPLATE_ID = window.ww?.vueStartingState?.templateIds?.presentation

export const useDashMakeStore = defineStore('dashMakeStore', () => {
  const userAccountsStore = useUserAccountsStore()
  const dashAccountsStore = useDashAccountsStore()
  const dashCollectionsStore = useDashCollectionsStore()
  const dashStore = useDashStore()
  const dashGalleryTemplatesStore = useDashGalleryTemplatesStore()
  const dashSuggestedTemplatesStore = useDashSuggestedTemplatesStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const dashMakeButtonStore = useDashMakeButtonStore()
  const magicPadletPanelStore = useMagicPadletPanelStore()
  const experimentsStore = useExperimentsStore()

  // State
  const currentFilter = ref<DashMakePageFilter>(DashMakePageFilter.START_FROM_SCRATCH)
  const makeSectionsElementRef = ref<HTMLElement[]>([])
  const { selectedElementKey, scrollToElement } = useObserveVisibleElementOnScroll(makeSectionsElementRef)
  const isCreatingWall = ref(false)
  const personalLibraryWallTemplates = ref<WallTemplate[]>([])
  const nonPersonalLibraryWallTemplatesByLibraryId = ref<Record<LibraryId, WallTemplate[]>>({})
  const clickedSidePane = ref<boolean>(false)

  // Getters
  const observableMakeSection = computed<string | undefined>(() => selectedElementKey.value)
  const isFetchingMakePageData = computed<boolean>(() => userAccountsStore.isFetchingWallCreatableLibraries)
  const isCurrentLibraryNotCreatable = computed(
    (): boolean =>
      !(dashAccountsStore.currentLibrary == null) &&
      !dashAccountsStore.wallCreatableLibrariesArray.includes(dashAccountsStore.currentLibrary),
  )
  const cannotMakeWallBecauseOfTeamLibraryPermissions = computed(
    (): boolean => dashAccountsStore.isCurrentLibraryTeam && isCurrentLibraryNotCreatable.value,
  )
  const cannotMakeWallBecauseOfSchoolLibraryPermissions = computed(
    (): boolean => dashAccountsStore.isCurrentLibrarySchool && isCurrentLibraryNotCreatable.value,
  )
  const cannotMakeWallBecauseOfClassroomLibraryPermissions = computed(
    (): boolean => dashAccountsStore.isCurrentLibraryClassroom && isCurrentLibraryNotCreatable.value,
  )
  const cannotMakeWallBecauseOfGroupFolderPermissions = computed((): boolean => {
    // Check if we're currently on a user group tab
    const activeUserGroup = dashCollectionsStore.activeUserGroup
    if (activeUserGroup == null) return false

    // Check if user has permission to create walls in this group
    const currentLibrary = dashAccountsStore.currentLibrary
    if (currentLibrary == null) return false

    // User can create if they're a teacher in the current library
    const currentUserRole = dashAccountsStore.userRolesByLibraryId[currentLibrary.id]
    const canEdit =
      currentUserRole === LibraryMembershipRole.Owner ||
      currentUserRole === LibraryMembershipRole.Admin ||
      currentUserRole === LibraryMembershipRole.Teacher

    // User can also create if the group allows anonymous posting
    const hasStudentAddPadletPermission = activeUserGroup.allowStudentAddPadlet === true

    // If feature flag is off, then permissions don't exist
    const classesV1FeatureFlagOff = !isAppUsing('classesV1')

    // Can make wall if either condition is true
    return !(canEdit || hasStudentAddPadletPermission || classesV1FeatureFlagOff)
  })
  const cannotMakeWallBecauseOfTenantPermissions = computed(
    (): boolean => dashAccountsStore.currentUserOptions?.canMakeWallInTenant === false,
  )
  const atQuota = computed(
    (): boolean =>
      (dashAccountsStore.currentLibrary == null && !dashAccountsStore.currentUser.quota.can_make) ||
      (!(dashAccountsStore.currentLibrary == null) && dashAccountsStore.currentLibrary.quota?.quotaHit),
  )
  const currentLibrarySubscriptionStatus = computed((): SubscriptionStatus | null => {
    if (dashAccountsStore.currentLibrary === null || dashAccountsStore.currentLibrary === undefined) return null
    if (dashAccountsStore.currentLibrary.isDeprecatedFreeTier) return null
    if (dashAccountsStore.currentLibrary.membershipTier === 'gold') return SubscriptionStatus.Paying
    if (dashAccountsStore.currentLibrary.membershipTier === 'trial') return SubscriptionStatus.OnTrial
    if (dashAccountsStore.currentLibrary.isPlanCancelled) return SubscriptionStatus.Cancelled
    if (dashAccountsStore.currentLibrary.isTrialExpired as boolean) return SubscriptionStatus.TrialExpired
    return null
  })
  const cannotMakeWall = computed(
    (): boolean =>
      isCurrentLibraryNotCreatable.value ||
      cannotMakeWallBecauseOfTenantPermissions.value ||
      cannotMakeWallBecauseOfGroupFolderPermissions.value ||
      atQuota.value ||
      currentLibrarySubscriptionStatus.value === SubscriptionStatus.Cancelled ||
      currentLibrarySubscriptionStatus.value === SubscriptionStatus.TrialExpired,
  )
  const shouldDisableCreateWallButton = computed((): boolean => cannotMakeWall.value || isCreatingWall.value)
  const shouldDisableCreateSandboxButton = computed((): boolean => {
    if (isPartOfUnlimitedSandboxesEvent()) {
      if (dashAccountsStore.currentLibrary == null && !dashStore.xTenantFrozen) {
        return false
      } else if (dashAccountsStore.currentLibrary != null) {
        if (dashAccountsStore.isCurrentLibraryFrozen || dashAccountsStore.currentLibrary.isPlanCancelled) return true
        return false
      }
    }
    return shouldDisableCreateWallButton.value
  })
  const shouldShowUpgradeModalForBoards = computed(
    () =>
      isAppUsing('makePageUpgradeModal') &&
      shouldDisableCreateWallButton.value &&
      dashAccountsStore.isCurrentLibrarySolo,
  )
  const shouldShowUpgradeModalForSandbox = computed(
    () =>
      isAppUsing('makePageUpgradeModal') &&
      shouldDisableCreateSandboxButton.value &&
      dashAccountsStore.isCurrentLibrarySolo,
  )
  const cannotMakeWallAndCannotUpgrade = computed((): boolean => {
    if (
      dashAccountsStore.isNativeAccount &&
      (dashAccountsStore.currentLibrary == null ||
        (dashAccountsStore.isCurrentUserLibraryOwner && !isAppUsing('schoolLibraryFreeze')))
    )
      return false
    if (dashAccountsStore.isAtLeastBackpackAdmin) return false

    return cannotMakeWall.value
  })
  const currentLibraryWallTemplates = computed<WallTemplate[]>(() => {
    return dashAccountsStore.currentLibrary === null
      ? personalLibraryWallTemplates.value
      : nonPersonalLibraryWallTemplatesByLibraryId.value[dashAccountsStore.currentLibrary.id]
  })
  const isABTestSimplifiedMakePageLayoutVariant = computed(() => {
    return (
      experimentsStore.makePageLayoutExistingUsersExperimentVariant ===
        MakePageLayoutExistingUsersExperimentVariant.VariantASimplified ||
      experimentsStore.makePageLayoutNewTeachersExperimentVariant ===
        MakePageLayoutNewTeachersExperimentVariant.VariantASimplified ||
      experimentsStore.makePageSimplifiedLayoutExperimentVariant ===
        MakePageSimplifiedLayoutExperimentVariant.VariantASimplified
    )
  })
  const isABTestPreviewCardsMakePageLayoutVariant = computed(() => {
    return (
      experimentsStore.makePageLayoutExistingUsersExperimentVariant ===
        MakePageLayoutExistingUsersExperimentVariant.VariantBPreviewCards ||
      experimentsStore.makePageLayoutNewTeachersExperimentVariant ===
        MakePageLayoutNewTeachersExperimentVariant.VariantBPreviewCards
    )
  })
  const unableToCreateWallText = computed((): string => {
    if (dashStore.xTenantFrozen) {
      return __('Your tenant has been frozen.')
    }

    if (dashAccountsStore.isCurrentLibraryFrozen) {
      return __('Your school has been frozen.')
    }

    if (cannotMakeWallBecauseOfTeamLibraryPermissions.value) {
      // Cannot make walls because of permissions
      return __('You do not have Maker permissions in this team.')
    }

    if (cannotMakeWallBecauseOfSchoolLibraryPermissions.value || cannotMakeWallBecauseOfTenantPermissions.value) {
      return __('An admin has disabled permissions for making new padlets.')
    }

    if (cannotMakeWallBecauseOfClassroomLibraryPermissions.value) {
      return __('A teacher has disabled permissions for making new padlets.')
    }

    if (cannotMakeWallBecauseOfGroupFolderPermissions.value) {
      return __('Only teachers can make padlets in this group.')
    }

    const currentLibrary = dashAccountsStore.currentLibrary

    // At quota for personal libraries (accounts)
    if (currentLibrary == null) {
      if (isPartOfUnlimitedSandboxesEvent()) {
        if (dashAccountsStore.currentUser.eligible_for_self_serve_trial) {
          const trialDays = dashAccountsStore.currentUser.trial_duration_days ?? 0
          return __(
            'You’ve reached your quota for boards. Start a free %{trialDays}-day trial to create more. You can create unlimited sandboxes until %{date}.',
            {
              trialDays,
              date: unlimitedSandboxesEventUtcEndDate({
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              }),
            },
          )
        } else {
          return __(
            'You’ve reached your quota for boards but you can upgrade to create more. You can create unlimited sandboxes until %{date}.',
            {
              date: unlimitedSandboxesEventUtcEndDate({
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              }),
            },
          )
        }
      }
      return __('You’ve reached your padlet quota. <br> Upgrade to continue making padlets.')
    }

    const currentUserLibraryRole = dashAccountsStore.userRolesByLibraryId[currentLibrary.id]

    /**
     * For trial libraries (team and classroom), we don't limit the quota, so there's no need to check for quota
     */

    // Trial expired
    if (currentLibrarySubscriptionStatus.value === SubscriptionStatus.TrialExpired) {
      if (dashAccountsStore.isCurrentLibraryClassroom) {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your classroom’s free trial has ended. <br> Upgrade to continue making new padlets.')
        } else {
          return __('Your classroom’s free trial has ended.')
        }
      } else {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your team’s free trial has ended. <br> Upgrade to continue making new padlets.')
        } else {
          return __('Your team’s free trial has ended.')
        }
      }
    }

    // Subscription cancelled
    if (currentLibrarySubscriptionStatus.value === SubscriptionStatus.Cancelled) {
      if (dashAccountsStore.isCurrentLibraryClassroom) {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your classroom’s subscription has been cancelled. <br> Upgrade to continue making new padlets.')
        } else {
          return __('Your classroom’s subscription has been cancelled.')
        }
      } else if (dashAccountsStore.isCurrentLibrarySchool) {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your school’s subscription has been cancelled. <br> Upgrade to continue making new padlets.')
        } else {
          return __('Your school’s subscription has been cancelled.')
        }
      } else {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your team’s subscription has been cancelled. <br> Upgrade to continue making new padlets.')
        } else {
          return __('Your team’s subscription has been cancelled.')
        }
      }
    }

    /**
     * For non-trial libraries.
     */

    // At quota school libraries
    if (dashAccountsStore.isCurrentLibrarySchool) {
      if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
        return __('Your school has reached its padlet quota. <br> Upgrade to continue making padlets.')
      } else {
        return __('Your school has reached its padlet quota.')
      }
    }

    // At quota for team libraries
    if (dashAccountsStore.isCurrentLibraryTeam) {
      if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
        return __('Your team has reached its padlet quota. <br> Upgrade to continue making padlets.')
      } else {
        return __('Your team has reached its padlet quota.')
      }
    }

    // At quota for personal libraries (accounts)
    if (isPartOfUnlimitedSandboxesEvent()) {
      if (dashAccountsStore.currentUser.eligible_for_self_serve_trial) {
        const trialDays = dashAccountsStore.currentUser.trial_duration_days ?? 0
        return __(
          'You’ve reached your quota for boards. Start a free %{trialDays}-day trial to create more. You can create unlimited sandboxes until %{date}.',
          {
            trialDays,
            date: unlimitedSandboxesEventUtcEndDate({
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            }),
          },
        )
      } else {
        return __(
          'You’ve reached your quota for boards but you can upgrade to create more. You can create unlimited sandboxes until %{date}.',
          {
            date: unlimitedSandboxesEventUtcEndDate({
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            }),
          },
        )
      }
    }
    return __('You’ve reached your padlet quota. <br> Upgrade to continue making padlets.')
  })

  // Actions
  const convertExperimentGoals = (): void => {
    AbApi.convertAbExperimentGoal('make_page_layout_new_teachers_experiment', 'created_padlet')
    AbApi.convertAbExperimentGoal('make_page_layout_existing_users_experiment', 'created_padlet')
    AbApi.convertAbExperimentGoal('make_page_simplified_layout_experiment', 'created_padlet')
  }

  async function initialize({
    currentUserWallTemplates,
    libraryToWallTemplates,
  }: {
    currentUserWallTemplates: WallTemplate[]
    libraryToWallTemplates: Record<LibraryId, WallTemplate[]>
  }): Promise<void> {
    personalLibraryWallTemplates.value = currentUserWallTemplates
    nonPersonalLibraryWallTemplatesByLibraryId.value = libraryToWallTemplates
    void dashGalleryTemplatesStore.fetchGalleryTemplates()
    void dashSuggestedTemplatesStore.fetchSuggestedTemplatesData()
  }

  const setMakeSectionsElementRef = (els: HTMLElement[]): void => {
    makeSectionsElementRef.value = els
  }

  const setCurrentFilter = (filter: DashMakePageFilter): void => {
    currentFilter.value = filter
  }

  const setCurrentFilterAndScrollToEl = (filter: DashMakePageFilter): void => {
    setCurrentFilter(filter)
    scrollToElement(filter)
  }

  const setClickedSidePane = (clicked: boolean): void => {
    clickedSidePane.value = clicked
  }

  const setIsCreatingWall = (newState: boolean): void => {
    isCreatingWall.value = newState
  }

  // Processing and retry stages
  async function createWall({
    viz,
    groupBySection,
    sourceId,
    createdFrom = WallCreatedFrom.Blank,
    creationEntrypoint,
    title,
    description,
  }: {
    viz: string
    groupBySection?: boolean
    sourceId?: string
    createdFrom?: string
    creationEntrypoint?: string
    title?: string
    description?: string
  }): Promise<void> {
    isCreatingWall.value = true

    try {
      // Create the payload for WallApi.create dynamically

      const createPayload = {
        viz,
        libraryId: dashAccountsStore.currentLibrary?.id,
        groupBySection,
        createdFrom,
        ...(sourceId !== null && { sourceId }),
        ...(creationEntrypoint !== null && { creationEntrypoint }),
        ...(title !== null && { title }),
        ...(description !== null && { description }),
      }

      const response = await WallApi.create(createPayload)

      // If the user has clicked the dashed make button, check if there are any actions to perform
      await dashMakeButtonStore.handleDashMakeWallCreationActions(response.id as number)

      // To show the onboarding flow when user first visit the padlet
      setCookie('first_view', 'true', { path: `/${response.address as string}` })
      navigateTo(response.links.show as string, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        isCreatingWall.value = false
      }
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating padlet'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      isCreatingWall.value = false
    }
  }

  async function createGridWithoutSectionsWall(
    properties: WallCreationMetadata = { createdFrom: WallCreatedFrom.Blank },
  ): Promise<void> {
    await createWall({ viz: 'grid', groupBySection: false, ...properties })
  }

  async function createWhiteboard(
    properties: WallCreationMetadata = { createdFrom: WallCreatedFrom.Whiteboard },
  ): Promise<void> {
    await createWall({ viz: 'whiteboard', ...properties })
  }

  async function createWallWithDiscussionBoard(): Promise<void> {
    // This was taken from the discussion board template https://padlet.com/gallerytemplates/discussion-topic-goes-here-cd8if1zoeurjmn4o
    const DISCUSSION_BOARD_TEMPLATE_ID = 205981623
    isCreatingWall.value = true
    try {
      const response = await WallApi.createFromTemplate(DISCUSSION_BOARD_TEMPLATE_ID, {
        jsonData: {
          title: __('[discussion topic goes here]'),
          description: __('Post your response to the discussion topic by clicking the plus button below.'),
          sourceId: GuidedTemplateType.DiscussionBoard,
          createdFrom: WallCreatedFrom.GuidedTemplate,
          with_same_privacy: false,
          with_original_authors: false,
          library_id: dashAccountsStore.currentLibrary?.id ?? null,
        },
      })
      const responseData = response?.data as JsonAPIResource<WallCreationFromTemplateResult>
      const wallCreatedFromTemplateUrl = (response?.data as JsonAPIResource<WallCreationFromTemplateResult>)?.attributes
        .url

      // If the user has clicked the dashed make button, check if there are any actions to perform
      await dashMakeButtonStore.handleDashMakeWallCreationActions(responseData.id as number)

      navigateTo(wallCreatedFromTemplateUrl, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        isCreatingWall.value = false
      }
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating padlet'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      isCreatingWall.value = false
    }
  }
  async function createWallWithExitTicket(): Promise<void> {
    // TODO: change discussion board template to exit ticket template
    // This was taken from the discussion board template https://padlet.com/gallerytemplates/discussion-topic-goes-here-cd8if1zoeurjmn4o
    isCreatingWall.value = true
    try {
      const wallParams = {
        title: p__(
          "An Exit ticket is an informal activity at the end of class used to quickly assess students' understanding of lesson objectives. Students must submit their responses or reflections before leaving, like a ticket needed to exit the classroom",
          '[exit ticket title goes here]',
        ),
        createdFrom: WallCreatedFrom.GuidedTemplate,
        sourceId: GuidedTemplateType.ExitTicket,
      }
      if (process.env.NODE_ENV === 'production') {
        const EXIT_TICKET_TEMPLATE_ID = 220380045
        const createFromTemplatePayload = {
          ...wallParams,
          with_same_privacy: false,
          with_original_authors: false,
          library_id: dashAccountsStore.currentLibrary?.id ?? null,
        }
        const response = await WallApi.createFromTemplate(EXIT_TICKET_TEMPLATE_ID, {
          jsonData: createFromTemplatePayload,
        })
        const wallCreatedFromTemplateUrl = (response?.data as JsonAPIResource<WallCreationFromTemplateResult>)
          ?.attributes.url
        navigateTo(wallCreatedFromTemplateUrl, { target: device.electronApp ? '_blank' : '_self' })
      } else {
        // create new wall in development
        const createPayload = {
          ...wallParams,
          viz: 'grid', // Using grid layout for exit ticket
          libraryId: dashAccountsStore.currentLibrary?.id ?? undefined,
        }
        const response = await WallApi.create(createPayload)
        navigateTo(response.links.show, { target: device.electronApp ? '_blank' : '_self' })
      }

      if (device.electronApp) {
        isCreatingWall.value = false
      }
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating padlet'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      isCreatingWall.value = false
    }
  }

  async function createWallWithDemoPadlet(): Promise<void> {
    isCreatingWall.value = true
    try {
      const response = await WallApi.createFromDemo({
        jsonData: {
          library_id: dashAccountsStore.currentLibrary?.id ?? null,
        },
      })
      const wallCreatedFromDemoPadUrl = (response?.data as JsonAPIResource<WallCreationFromTemplateResult>)?.attributes
        .url
      navigateTo(wallCreatedFromDemoPadUrl, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        isCreatingWall.value = false
      }
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating padlet'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      isCreatingWall.value = false
    }
  }

  async function createWallFromTemplate(
    wallTemplate: WallTemplate,
    properties: WallCreationMetadata = { createdFrom: WallCreatedFrom.Blank },
  ): Promise<void> {
    isCreatingWall.value = true
    try {
      // Format for template title is `wall-title - August 23, 2023`
      const currentDateLongFormat = formatDate(new Date(), { month: 'long', year: 'numeric', day: 'numeric' })
      const title = wallTemplate.title ? `${wallTemplate.title} - ${currentDateLongFormat}` : undefined
      const response = await WallApi.createFromTemplate(wallTemplate.id, {
        jsonData: {
          title,
          description: wallTemplate.description,
          with_posts: true,
          with_same_privacy: false,
          with_original_authors: false,
          library_id: dashAccountsStore.currentLibrary?.id ?? null,
          source_id: wallTemplate.id,
          created_from: properties.createdFrom,
          creation_entrypoint: properties.creationEntrypoint,
          send_notification: PRESENTATION_TEMPLATE_ID !== wallTemplate.id,
        },
      })
      const responseData = response?.data as JsonAPIResource<WallCreationFromTemplateResult>
      const wallCreatedFromTemplateUrl = (response?.data as JsonAPIResource<WallCreationFromTemplateResult>)?.attributes
        .url

      // If the user has clicked the dashed make button, check if there are any actions to perform
      await dashMakeButtonStore.handleDashMakeWallCreationActions(responseData.id as number)

      navigateTo(wallCreatedFromTemplateUrl, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        isCreatingWall.value = false
      }
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating padlet'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      isCreatingWall.value = false
    }
  }
  // Similar to createWallFromTemplate, however with different jsonData.
  // TODO: update WallProps for onboarding upon click.
  async function createWallFromGalleryTemplate(wallGalleryTemplate: WallGalleryTemplate): Promise<void> {
    isCreatingWall.value = true
    try {
      const response = await WallApi.createFromTemplate(wallGalleryTemplate.id, {
        jsonData: {
          with_posts: true,
          with_same_privacy: false,
          with_original_authors: false,
          library_id: dashAccountsStore.currentLibrary?.id ?? null,
          created_from: WallCreatedFrom.GalleryTemplate,
          source_id: wallGalleryTemplate.galleryTemplateId,
        },
      })
      const wallCreatedFromTemplateUrl = (response?.data as JsonAPIResource<WallCreationFromTemplateResult>)?.attributes
        .url
      navigateTo(wallCreatedFromTemplateUrl, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        isCreatingWall.value = false
      }
      trackEvent('Template', 'Create wall from gallery template', wallGalleryTemplate.title)
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating padlet'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      isCreatingWall.value = false
    }
  }

  async function createBoardFromAiMakeSuggestion(suggestion: AiMakeSuggestion): Promise<void> {
    isCreatingWall.value = true
    try {
      await magicPadletPanelStore.generateCustomBoardFromSuggestion(
        suggestion,
        dashAccountsStore.currentLibrary?.id,
        true,
      )
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating padlet'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
    }
    isCreatingWall.value = false
  }

  async function createWhiteboardFromAiMakeSuggestion(suggestion: AiMakeSuggestion): Promise<void> {
    isCreatingWall.value = true
    try {
      // Create whiteboard using proper WallApi with JSON
      const response = await WallApi.create({
        viz: 'whiteboard',
        libraryId: dashAccountsStore.currentLibrary?.id,
        title: suggestion.name,
        description: suggestion.short_description,
        createdFrom: WallCreatedFrom.AiMakeSuggestionWhiteboard,
      })

      const wallAddress = response.address as string
      const wallHashid = response.hashid as string

      // Populate whiteboard with Venn diagram data using the wall hashid
      await PadletApi.AiMakeSuggestionApi.populateWhiteboard(wallHashid, suggestion)

      // Navigate to the created wall
      setCookie('first_view', 'true', { path: `/${wallAddress}` })
      navigateTo(response.links.show as string, { target: '_self' })
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating whiteboard'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
    } finally {
      isCreatingWall.value = false
    }
  }

  async function createWhiteboardFromTemplate(
    templateType: WhiteboardCardTemplateType,
    properties: WallCreationMetadata,
  ): Promise<void> {
    isCreatingWall.value = true
    try {
      const response = await WallApi.create({
        viz: 'whiteboard',
        libraryId: dashAccountsStore.currentLibrary?.id,
        createdFrom: properties.createdFrom,
        sourceId: templateType,
        creationEntrypoint: properties.creationEntrypoint,
      })
      const wallAddress = response.address as string
      const wallHashid = response.hashid as string
      await PadletApi.WhiteboardCardTemplateApi.applyTemplate(wallHashid, templateType)
      await dashMakeButtonStore.handleDashMakeWallCreationActions(response.id as number)
      setCookie('first_view', 'true', { path: `/${wallAddress}` })
      navigateTo(response.links.show as string, { target: device.electronApp ? '_blank' : '_self' })
    } catch (e) {
      captureException(e)
      globalSnackbarStore.setSnackbar({
        message: __('Error creating padlet'),
        notificationType: SnackbarNotificationType.error,
        timeout: 8000,
      })
      isCreatingWall.value = false
    }
  }

  return {
    // State
    currentFilter,
    isCreatingWall,
    clickedSidePane,

    // Getters
    isFetchingMakePageData,
    isABTestSimplifiedMakePageLayoutVariant,
    isABTestPreviewCardsMakePageLayoutVariant,
    cannotMakeWall,
    cannotMakeWallBecauseOfGroupFolderPermissions,
    shouldDisableCreateWallButton,
    shouldDisableCreateSandboxButton,
    shouldShowUpgradeModalForBoards,
    shouldShowUpgradeModalForSandbox,
    cannotMakeWallAndCannotUpgrade,
    currentLibraryWallTemplates,
    unableToCreateWallText,
    observableMakeSection,

    // Actions
    convertExperimentGoals,
    initialize,
    setIsCreatingWall,
    setCurrentFilter,
    setCurrentFilterAndScrollToEl,
    setClickedSidePane,
    createGridWithoutSectionsWall,
    createWhiteboard,
    createWall,
    createWallWithDiscussionBoard,
    createWallWithExitTicket,
    createWallWithDemoPadlet,
    createWallFromTemplate,
    createWallFromGalleryTemplate,
    createBoardFromAiMakeSuggestion,
    createWhiteboardFromAiMakeSuggestion,
    createWhiteboardFromTemplate,

    // Refs for scrolling to different sections
    makeSectionsElementRef,
    setMakeSectionsElementRef,
  }
})
